<template>
    <div class="form-group">
        <div class="d-flex align-items-center">
            <label :for="id">
                {{ label }}
            </label>
            <div v-if="info" class="with-tooltip">
                <img class="info-tooltip" src="../../assets/img/question.svg">
                <div class="tooltiptext">
                    {{ info }}
                </div>
            </div>
        </div>
        <div class="bottom-link"
             v-if="selectedValues && selectedValues.length"
             @click="clearDates">
            Clear all dates
        </div>
        <div class="input-wrapper"
             v-click-outside="close">
            <input :id="id"
                   ref="input"
                   type="text"
                   :style="{...inputStyle, height}"
                   :name="name"
                   :disabled="disabled"
                   v-validate="{required}"
                   v-model="inputValue"
                   @click="toggle"
                   readonly
                   :placeholder="multipleSelect ? '' : '__ /__ /__'">
            <div class="selected-dates-list"
                 ref="chips">
                <chip v-for="(date, index) of selectedValues"
                      class="chip"
                      :width=chipWidth
                      :key="index"
                      :label="format(date)"
                      @close="removeDate(index)"
                >
                </chip>
            </div>
            <img src="../../assets/img/calendar.svg"
                 @click="toggle">
           <calendar v-if="isOpened"
                     ref="popup"
                     :class="['calendar-popup', mobilePosition ? 'left' : 'right']"
                     :calendar-date="date"
                     :dates="dates"
                     :value="value"
                     :selected-values="selectedValues"
                     :multiple-select="multipleSelect"
                     :show-triange="showTriangle"
                     :style="popupStyle"
                     :triangle-style="{...triangleStyle, ...arrowStyle, 'left': arrowStyleLeft}"
                     @select-date="selectDate">
           </calendar>
        </div>
        <div class="error">{{ errors.first(name) }}</div>
    </div>
</template>

<script>
    import ClickOutside from 'vue-click-outside';
    import {DatePopupMixin} from '../../mixins/DatePopupMixin';
    import {mapGetters} from 'vuex';
    import * as moment from 'moment';
    import {MOMENT_BACKEND_DATE_FORMAT} from '../../constants/dateFormats';
    import Calendar from './Calendar';

    const CHIP_HEIGHT = 32;
    const ICON_WIDTH = 40;
    const GAP = 8;
    const DEFAULT_HEIGHT = 49;

    export default {
        name: 'DatePicker',
        mixins: [DatePopupMixin(function() {
            return this.$refs.popup.$el
        })],
        components: {
            Calendar,
            Chip: () => import('./Chip')
        },
        directives: {ClickOutside},
        inject: ['$validator'],
        props: {
            id: {
                type: String,
                required: true
            },
            label: {
                type: String
            },
            info: {
                type: String
            },
            multipleSelect: {
                type: Boolean,
                default: false
            },
            value: [String, Date, Array],
            popupStyle: {
                type: Object,
                default: () => ({})
            },
            triangleStyle: {
                type: Object,
                default: () => ({})
            },
            showTriangle: {
                type: Boolean,
                default: true
            },
            styleInput: {
                type: Object,
                default: () => ({})
            },
            chipWidth: {
                type: [Number,String],
                default: 120
            },
            defaultValue: [String, Date, Array],
            disabled: {
                type: Boolean,
                default: false
            },
            dates: {
                type: [Object, Array],
                required: false
            },
            required: {
                type: Boolean,
                default: false
            }
        },
        $_veeValidate: {
            name() {
                return this.name;
            },
            value() {
                return this.inputValue;
            }
        },
        data() {
            return {
                date: new Date(),
                isOpened: false,
                selectedValues: [],
                inputValue: '',
                name: !this.multipleSelect ? 'date-picker-' + Math.random() * 10 : 'multiple',
                height: DEFAULT_HEIGHT + 'px',
                mobilePosition: false
            }
        },
        computed: {
            ...mapGetters({
                dateFormat: 'dateFormatYearShort'
            }),
            arrowStyleLeft() {
              return this.triangleStyle && this.triangleStyle.left
                  ? this.triangleStyle.left
                  : (this.mobilePosition ? '5%' : '90%');
            },
            inputStyle() {
                let style = { ...this.styleInput };
                if (this.multipleSelect) {
                    style.color = 'white';
                }
                return style;
            }
        },
        watch: {
            value: {
                async handler(value) {
                    if (value) {
                        if (!this.multipleSelect) {
                            this.date = moment(this.value, MOMENT_BACKEND_DATE_FORMAT).toDate();
                            await this.$nextTick();
                            this.setDate(this.date);
                        } else {
                            this.selectedValues = value.map(date => moment(date, MOMENT_BACKEND_DATE_FORMAT).toDate());
                        }
                    } else {
                        this.inputValue = ''
                    }
                },
                immediate: true
            },
            selectedValues: {
                async handler(selectedValues) {
                    if (selectedValues) {
                        if (selectedValues.length) {
                            await this.$nextTick();
                            const inputWidth = this.$refs.input.getBoundingClientRect().width;
                            const chipsPerRow = Math.floor((inputWidth - ICON_WIDTH) / this.chipWidth);
                            const rows = Math.ceil(selectedValues.length / chipsPerRow);
                            const height = rows * (CHIP_HEIGHT + GAP) + GAP;
                            this.height = height + 'px';
                        }
                        else {
                            this.height = 49 + 'px';
                        }
                    }
                },
                immediate: true
            },
            defaultValue: {
                handler() {
                    if (this.defaultValue && !this.value && !this.multipleSelect) {
                        this.date = moment(this.defaultValue, MOMENT_BACKEND_DATE_FORMAT).toDate();
                    }
                },
                immediate: true
            },
            isOpened() {
                if (!this.inputValue) {
                    let date = new Date(this.date);
                    date.setDate(date.getDate() + 1);
                    this.setDate(date);
                }
            }
        },
        methods: {
            setDate(date) {
                if (!this.multipleSelect) {
                    this.inputValue = this.format(date);
                    this.$emit('input', moment(date).format(MOMENT_BACKEND_DATE_FORMAT));
                }
            },
            toggle(e) {
                this.mobilePosition = window.innerWidth/2 > e.clientX;
                this.isOpened = !this.isOpened;
            },
            close(event) {
                if (event) {
                    event.stopPropagation();
                }
                this.isOpened = false;
            },
            selectDate(date) {
                this.date = date;
                this.setDate(this.date);
                if (this.multipleSelect) {
                    if (!this.selectedValues.find(date => moment(date).isSame(this.date, 'day'))
                    ) {
                        this.selectedValues.push(this.date);
                        this.inputValue = this.date;
                        this.$emit('input', this.selectedValues.map(date => moment(date).format(MOMENT_BACKEND_DATE_FORMAT)));
                    }
                } else {
                    this.close();
                }
            },
            format(date) {
                return moment(date).format(this.dateFormat.toUpperCase());
            },
            async removeDate(index) {
                const newSelectedDates = this.selectedValues.filter((date, i) => i !== index);
                this.selectedValues = [];
                await this.$nextTick();
                this.selectedValues = newSelectedDates;
                this.$emit('input', this.selectedValues.map(date => moment(date).format(MOMENT_BACKEND_DATE_FORMAT)));
            },
            clearDates() {
                this.selectedValues = [];
                this.$emit('input', this.selectedValues);
            }
        }
    }
</script>

<style lang="scss" scoped>
    @import "../../assets/colors";

    $paddingTop: 5px;
    .form-group {
        min-width: 130px;
        width: 100%;
        .input-wrapper {
            position: relative;
            color: $Dark;
            input::placeholder {
                color: $Dark;
            }
            img {
                cursor: pointer;
                position: absolute;
                right: 15px;
                top: 15px;
                z-index: 3;
                @media screen and (max-width: 425px) {
                    right: 5px;
                    top: 5px;
                }
            }
        }
    }

    .selected-dates-list {
        position: absolute;
        top: 8px;
        left: 8px;
        display: flex;
        flex-wrap: wrap;
        z-index: 2;

        .chip {
            z-index: 4;
            margin: 0 8px 8px 0;
        }
    }

    .bottom-link {
        position: absolute;
        top: 2px;
        right: 0;
    }

    .with-tooltip {
        display: flex;
        justify-content: center;
        align-items: center;
        padding-left: 1px;
    }

    .calendar-popup {
        position: absolute;
        margin-bottom: 8px;
        /deep/.date-picker {
            @media screen and (max-width: 480px) {
                width: 290px;
                right: -5px !important;
            }
            @media screen and (max-width: 425px) {
                width: 290px;
                right: -15px !important;
            }
        }
    }

    .right {
        right: -5px;
        @media all and (-ms-high-contrast: none), (-ms-high-contrast: active) {
            right: -10px;
        }
    }

    .left {
        left: -5px;
        @media all and (-ms-high-contrast: none), (-ms-high-contrast: active) {
            left: -10px;
        }
    }
</style>


// WEBPACK FOOTER //
// src/components/controls/DatePicker.vue